import { useEffect, useState } from "react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from "recharts";
import { useApp } from "@/context/AppContext";
import api from "@/lib/api";
import { formatPeriod, formatValue, STATUS_META } from "@/lib/helpers";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function ChartTooltip({ active, payload, label, unit }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-border bg-popover px-3 py-2 text-xs shadow-lg">
      <p className="font-medium">{formatPeriod(label)}</p>
      <p className="text-muted-foreground">{formatValue(payload[0].value, unit)} {unit}</p>
    </div>
  );
}

export default function Historicos() {
  const { companyIdsParam, selectedCompanyIds, companies } = useApp();
  const [kpis, setKpis] = useState([]);
  const [kpiId, setKpiId] = useState(null);
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/kpis").then((res) => {
      setKpis(res.data);
      if (res.data.length) setKpiId((prev) => prev || res.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!kpiId || !companyIdsParam) return;
    api
      .get(`/kpis/${kpiId}/history?company_ids=${companyIdsParam}`)
      .then((res) => setData(res.data));
  }, [kpiId, companyIdsParam]);

  const kpi = data?.kpi;
  const history = data?.history || [];
  const scopeLabel =
    selectedCompanyIds.length === companies.length ? "Todas las empresas (consolidado)"
    : selectedCompanyIds.length === 1
      ? companies.find((c) => c.id === selectedCompanyIds[0])?.name
      : `${selectedCompanyIds.length} empresas (consolidado)`;

  return (
    <div className="mx-auto max-w-[1200px] space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Históricos de Resultados</h1>
          <p className="mt-1 text-sm text-muted-foreground">{scopeLabel} · evolución por periodo</p>
        </div>
        <Select value={kpiId || ""} onValueChange={setKpiId}>
          <SelectTrigger className="h-9 w-[300px]" data-testid="history-kpi-select">
            <SelectValue placeholder="Selecciona indicador" />
          </SelectTrigger>
          <SelectContent>
            {kpis.map((k) => (
              <SelectItem key={k.id} value={k.id} data-testid={`history-kpi-option-${k.code}`}>
                {k.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="rounded-xl border border-border bg-card p-5" data-testid="history-chart">
        <div className="mb-4 flex items-center justify-between">
          <div>
            <h2 className="font-heading text-lg font-semibold">{kpi?.name}</h2>
            <p className="font-mono text-xs text-muted-foreground">{kpi?.formula}</p>
          </div>
          <Badge variant="outline">{kpi?.unit}</Badge>
        </div>
        <ResponsiveContainer width="100%" height={320}>
          <AreaChart data={history} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--info))" stopOpacity={0.35} />
                <stop offset="95%" stopColor="hsl(var(--info))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
            <XAxis dataKey="period" tickFormatter={formatPeriod} tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} axisLine={false} tickLine={false} width={56} />
            <Tooltip content={<ChartTooltip unit={kpi?.unit} />} />
            {kpi && <ReferenceLine y={kpi.green_threshold} stroke="hsl(var(--success))" strokeDasharray="4 4" />}
            {kpi && <ReferenceLine y={kpi.yellow_threshold} stroke="hsl(var(--warning))" strokeDasharray="4 4" />}
            <Area type="monotone" dataKey="value" stroke="hsl(var(--info))" strokeWidth={2.5} fill="url(#grad)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="overflow-hidden rounded-xl border border-border">
        <table className="w-full text-sm" data-testid="history-table">
          <thead className="bg-accent/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Periodo</th>
              <th className="px-4 py-3">Valor</th>
              <th className="px-4 py-3">Semáforo</th>
            </tr>
          </thead>
          <tbody>
            {history.map((h) => {
              const meta = STATUS_META[h.status];
              return (
                <tr key={h.period} className="border-t border-border">
                  <td className="px-4 py-2.5 font-medium">{formatPeriod(h.period)}</td>
                  <td className="px-4 py-2.5">{formatValue(h.value, kpi?.unit)} {kpi?.unit}</td>
                  <td className="px-4 py-2.5">
                    <Badge variant="outline" className={`${meta.bg} ${meta.text} ${meta.border}`}>{meta.label}</Badge>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
